import classNames from 'classnames';
import './ImageUploader.pcss';
import React, { MouseEvent, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { ReactComponent as TrashIcon } from 'assets/garbage.svg';
import { getFormatFromType } from '../../helpers/widget-helpers/use-common-widget-handlers';
import Spinner from '../Spinner';
import { CropArea } from '../CropArea/CropArea';
import { ReactComponent as Upload } from '../../../layout/BotBuilderSidebar/ActivePageSlideMenu/FacebookPageCustomizationPane/upload-icon.svg';
import { uploadImageWithUrl } from '@mm/state/actions/uploadActions';
import { StoreState } from '@mm/state/store-interfaces';

const useUploadImage = () => {
  const [isUploading, setIsUploading] = useState(false);
  const dispatch: Dispatch<StoreState> = useDispatch();

  const upload = async (
    file: File | Blob,
    onUpload: (assetId: number, url: string) => void,
  ) => {
    try {
      setIsUploading(true);
      const [id, url] = await dispatch(
        uploadImageWithUrl({
          content: file,
          extension: getFormatFromType(file.type),
        }),
      );
      setIsUploading(false);
      onUpload(id, url);
    } catch (error) {
      setIsUploading(false);
    }
  };

  return { isUploading, upload };
};

type Props = {
  onUpload: (assetId: number, url: string) => void;
  css?: { dropArea?: string };
  className?: string;
  needsFileModification?: boolean;
  file: string | null;
  disabled?: boolean;
  disableCrop?: boolean;
  withRemoveBtn?: boolean;
  onRemove?: () => void;
  newStyle?: boolean;
  fixedRatio?: boolean;
};

const ImageUploader = (props: Props) => {
  const {
    className,
    css = { dropArea: undefined },
    disabled,
    onUpload,
    file,
    disableCrop,
    withRemoveBtn,
    onRemove = () => {},
    needsFileModification,
    newStyle,
    fixedRatio,
  } = props;
  const { upload, isUploading } = useUploadImage();
  const [showCropper, setShowCropper] = useState(false);
  const accept = 'image/jpeg, image/gif, image/png';
  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      disabled,
      accept,
      onDrop: (files: File[]) => upload(files[0], onUpload),
    });

  const onClickCrop = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setShowCropper(true);
  };

  return (
    <div
      className={classNames('image-uploader', className, {
        'image-uploader--has-image': file,
        'image-uploader--active-drag': isDragActive,
        'image-uploader--rejected-drag': isDragReject,
        'image-uploader--disabled': disabled,
      })}
    >
      <div
        className={classNames(css.dropArea, 'image-uploader__drop-area')}
        {...getRootProps()}
      >
        {isUploading && (
          <div className="image-uploader__uploading-wrapper">
            <Spinner
              show={true}
              position="absolute"
              backgroundColor="none"
              size={30}
            />
          </div>
        )}

        {file && (
          <img
            alt="Cropped Asset"
            src={file}
            className="image-uploader__image"
          />
        )}

        <input {...getInputProps()} accept={accept} />

        {((!disabled && !newStyle) || newStyle) &&
          (file ? (
            <div className="image-uploader__edit-image-controls">
              <div className="image-uploader__edit-image-control">
                <i className="far fa-image fa-3x image-uploader__edit-image-control-icon" />

                <span>
                  Drop image file or
                  <span className="image-uploader__choose-file">
                    choose file
                  </span>
                </span>
              </div>

              {!disableCrop && (
                <div
                  onClick={onClickCrop}
                  className="image-uploader__edit-image-control"
                >
                  <i className="fa fa-crop fa-3x image-uploader__edit-image-control-icon" />

                  <span>Crop image</span>
                </div>
              )}

              <div className="image-uploader__edit-image-controls-blinder" />
            </div>
          ) : newStyle ? (
            <div
              className={classNames(
                'image-uploader__drop-area-text',
                'image-uploader__drop-area-text-new-style',
              )}
            >
              <Upload />
              <span>
                <span className="image-uploader__choose-file">Click</span>
                {''} or drag your logo to this area to upload
              </span>
            </div>
          ) : (
            <div className="image-uploader__drop-area-text">
              <i className="far fa-image fa-3x image-uploader__upload-icon" />
              <span>
                Drop image file or
                <span className="image-uploader__choose-file">choose file</span>
              </span>
            </div>
          ))}
      </div>

      {showCropper && (
        <CropArea
          needsFileModification={needsFileModification}
          imgUrl={file || undefined}
          onCropCallback={(blob: Blob) => upload(blob, onUpload)}
          closeCropper={() => setShowCropper(false)}
          fixedRatio={fixedRatio}
        />
      )}

      {file && withRemoveBtn && (
        <div className="image-uploader__remove-btn" onClick={onRemove}>
          <TrashIcon className="image-uploader__trash-icon" />
        </div>
      )}
    </div>
  );
};

export default React.memo(ImageUploader);
